export const CALCULATOR_ENGINE_CONTROL_REVISION_SELECTOR =
  "template[data-calculator-control-revision-v1]";

const CALCULATOR_ENGINE_CONTROL_SPECS = Object.freeze([
  Object.freeze({ field: "pets", id: "pets" }),
  Object.freeze({ field: "targetFish", id: "calculator-target-fish-control" }),
  Object.freeze({ field: "tradeOrigin", id: "calculator-trade-origin-control" }),
  Object.freeze({ field: "tradeDestination", id: "calculator-trade-destination-control" }),
]);

function requireIdentifier(value, name) {
  if (typeof value !== "string" || value.length === 0 || value.trim() !== value) {
    throw new TypeError(`${name} must be a non-empty identifier without surrounding whitespace`);
  }
  return value;
}

function controlElementId(element) {
  if (typeof element?.id === "string" && element.id !== "") {
    return element.id;
  }
  return element?.getAttribute?.("id") ?? null;
}

function parseRevisionPublication(value) {
  if (value === null || value === undefined) {
    return null;
  }
  if (
    typeof value !== "object"
    || Array.isArray(value)
    || Object.keys(value).sort().join(",") !== "activate,finalize,rollback,schemaVersion"
    || value.schemaVersion !== 1
    || typeof value.activate !== "function"
    || typeof value.finalize !== "function"
    || typeof value.rollback !== "function"
  ) {
    throw new TypeError(
      "calculator revision publication must contain schemaVersion, activate, finalize, rollback",
    );
  }
  return value;
}

export function parseCalculatorControlRevision(value, expectedModelRevision) {
  if (
    value === null
    || typeof value !== "object"
    || Array.isArray(value)
    || Object.keys(value).sort().join(",") !== "controls,modelRevision,schemaVersion"
  ) {
    throw new TypeError(
      "calculator control revision fields must be exactly controls, modelRevision, schemaVersion",
    );
  }
  if (value.schemaVersion !== 1) {
    throw new TypeError("calculator control revision schemaVersion must be 1");
  }
  const modelRevision = requireIdentifier(value.modelRevision, "control modelRevision");
  if (modelRevision !== expectedModelRevision) {
    throw new Error(
      `calculator control revision mismatch: expected ${expectedModelRevision}, got ${modelRevision}`,
    );
  }
  const controls = value.controls;
  const expectedFields = CALCULATOR_ENGINE_CONTROL_SPECS.map(({ field }) => field).sort();
  if (
    controls === null
    || typeof controls !== "object"
    || Array.isArray(controls)
    || Object.keys(controls).sort().join(",") !== expectedFields.join(",")
  ) {
    throw new TypeError(
      `calculator control revision controls must be exactly ${expectedFields.join(", ")}`,
    );
  }
  for (const { field, id } of CALCULATOR_ENGINE_CONTROL_SPECS) {
    const control = controls[field];
    if (controlElementId(control) !== id || typeof control?.cloneNode !== "function") {
      throw new TypeError(`calculator control revision ${field} must contain #${id}`);
    }
  }
  return Object.freeze({
    schemaVersion: 1,
    modelRevision,
    controls: Object.freeze({ ...controls }),
  });
}

export function parseCalculatorControlRevisionTemplate(template, expectedModelRevision) {
  const modelRevision = template?.getAttribute?.("data-model-revision");
  const children = Array.from(template?.content?.children ?? []);
  if (children.length !== CALCULATOR_ENGINE_CONTROL_SPECS.length) {
    throw new Error("calculator control revision requires exactly four fixed control roots");
  }
  const controls = {};
  for (const [index, { field, id }] of CALCULATOR_ENGINE_CONTROL_SPECS.entries()) {
    const control = children[index];
    if (controlElementId(control) !== id) {
      throw new Error(`calculator control revision root ${index + 1} must be #${id}`);
    }
    controls[field] = control;
  }
  return parseCalculatorControlRevision({
    schemaVersion: 1,
    modelRevision,
    controls,
  }, expectedModelRevision);
}

export function prepareCalculatorControlRevision(
  ownerDocument,
  value,
  expectedModelRevision,
) {
  const revision = parseCalculatorControlRevision(value, expectedModelRevision);
  const replacements = CALCULATOR_ENGINE_CONTROL_SPECS.map(({ field, id }) => {
    const current = ownerDocument?.getElementById?.(id) ?? null;
    if (!current || typeof current.replaceWith !== "function") {
      throw new Error(`calculator control revision cannot find live #${id}`);
    }
    const candidate = revision.controls[field].cloneNode(true);
    if (controlElementId(candidate) !== id || typeof candidate?.replaceWith !== "function") {
      throw new TypeError(`calculator control revision clone for ${field} is invalid`);
    }
    const placeholder = ownerDocument?.createComment?.(
      `calculator control revision ${revision.modelRevision} ${id}`,
    );
    if (!placeholder || typeof placeholder.replaceWith !== "function") {
      throw new TypeError("calculator control revision requires replaceable comment placeholders");
    }
    return { candidate, current, placeholder };
  });
  let state = "prepared";
  let detached = [];
  let installed = [];
  let publication = null;
  let publicationActivated = false;
  const withdrawInstalled = () => {
    const failures = [];
    for (const replacement of [...installed].reverse()) {
      try {
        replacement.candidate.replaceWith(replacement.placeholder);
      } catch (error) {
        failures.push(error);
      }
    }
    installed = [];
    if (failures.length > 0) {
      throw new AggregateError(
        failures,
        "calculator control revision candidate withdrawal failed",
      );
    }
  };
  const restoreDetached = () => {
    const failures = [];
    for (const replacement of [...detached].reverse()) {
      try {
        replacement.placeholder.replaceWith(replacement.current);
      } catch (error) {
        failures.push(error);
      }
    }
    detached = [];
    if (failures.length > 0) {
      throw new AggregateError(
        failures,
        "calculator control revision retained-control restoration failed",
      );
    }
  };
  const rollbackPreparedPublication = () => {
    const failures = [];
    try {
      withdrawInstalled();
    } catch (error) {
      failures.push(error);
    }
    if (publicationActivated) {
      try {
        publication?.rollback();
      } catch (error) {
        failures.push(error);
      }
      publicationActivated = false;
    }
    try {
      restoreDetached();
    } catch (error) {
      failures.push(error);
    }
    if (failures.length > 0) {
      throw new AggregateError(failures, "calculator control revision rollback failed");
    }
  };
  return Object.freeze({
    commit(revisionPublication = null) {
      if (state !== "prepared") {
        throw new Error("calculator control revision is not prepared for commit");
      }
      publication = parseRevisionPublication(revisionPublication);
      try {
        for (const replacement of replacements) {
          replacement.current.replaceWith(replacement.placeholder);
          detached.push(replacement);
        }
        if (publication !== null) {
          publicationActivated = true;
          publication.activate();
        }
        for (const replacement of replacements) {
          replacement.placeholder.replaceWith(replacement.candidate);
          installed.push(replacement);
        }
        state = "committed";
      } catch (error) {
        try {
          rollbackPreparedPublication();
        } catch (rollbackError) {
          state = "rolledBack";
          throw new AggregateError(
            [error, rollbackError],
            "calculator control revision publication and rollback failed",
          );
        }
        state = "rolledBack";
        throw new Error("calculator control revision could not be published", { cause: error });
      }
    },
    finalize() {
      if (state !== "committed") {
        throw new Error("calculator control revision is not committed");
      }
      publication?.finalize();
      publicationActivated = false;
      publication = null;
      detached = [];
      installed = [];
      state = "finalized";
    },
    rollback() {
      if (state === "rolledBack") {
        return;
      }
      if (state === "finalized") {
        throw new Error("finalized calculator control revision cannot be rolled back");
      }
      if (state === "committed") {
        try {
          rollbackPreparedPublication();
        } finally {
          state = "rolledBack";
        }
        return;
      }
      state = "rolledBack";
    },
  });
}
